import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormikValues } from 'formik';
import useAddRule from 'Hooks/query-hooks/global-rules/useAddRule';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import Layout from 'Components/Layout';
import AdvancedRuleForm from 'Components/AdvancedRuleForm';
import { setGroup } from 'Store';

type CreateAdvancedRuleLayoutProps = {
    parameterData: UseGetParametersResponse;
    listData: ListApiGetResponse;
    mode: 'add';
};

const CreateAdvancedRuleLayout = ({ parameterData, listData, mode }: CreateAdvancedRuleLayoutProps) => {
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const { addModal } = useReduxModal();
    const history = useHistory();
    const formRef = React.useRef<FormikValues>();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const dispatch = useDispatch();
    const { data: landlordInfo } = useGetLandlordInfo();

    const formInitialValues: AdvancedRuleFormSchema = {
        active: false,
        allowOverrides: false,
        comment: '',
        description: '',
        expression: {
            expText: '',
            inputs: {},
            functionCalls: []
        },
        priority: 1,
        group: [],
        ruleId: '',
        isScriptValidated: false
    };

    const onSuccess = (_data: any, { ruleId }: { ruleId: string }) => {
        dispatch(setGroup(''));
        addAlert({
            id: 'create-advanced-rule-success',
            content: t('addRuleSuccess', { ruleId }),
            status: 'success'
        });

        history.push('/global-rules');
    };

    const parseError = (err: any) => {
        const error = err.response;
        const customErrorMessage =
            error?.data?.error?.message || error?.data?.error?.msg?.error || error?.data?.error?.error?.message;
        return customErrorMessage
            ? t('addRuleErrorSpecific', { errMsg: customErrorMessage })
            : t('addRuleErrorGeneric');
    };

    const onError = (err: any) => {
        addModal({
            id: 'create-advanced-rule-error',
            status: 'error',
            title: t('Unable to save rule'),
            content: <>{parseError(err)}</>,
            cancelText: t('Close')
        });
        if (formRef.current) {
            formRef.current.setSubmitting(false);
        }
    };

    const mutation = useAddRule({
        templateRepositoryId: templateRepositoryId!,
        mutationOptions: {
            onSuccess,
            onError
        }
    });

    return (
        <Layout>
            <AdvancedRuleForm
                parameterData={parameterData}
                formInitialValues={formInitialValues}
                formRef={formRef}
                listData={listData}
                mode={mode}
                mutation={mutation}
                pageHeader={t('New Advanced Rule')}
                repoId={templateRepositoryId!}
                disabledInputFields={{
                    disableDefinitionChange: false,
                    disableOverrideChange: false,
                    hideOverrideChange: !landlordInfo?.isLandlord
                }}
            />
        </Layout>
    );
};

export default CreateAdvancedRuleLayout;
